import React, { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Lock, Unlock, Eye, EyeOff, Copy, CheckCircle, Upload, Edit2, Save, X, UserCircle } from 'lucide-react';
import { api } from '../lib/api';
import { getAvatarUrl, clearAvatarCache, getCachedAvatarUrl, batchGetAvatarUrls } from '../lib/avatarUtils';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarRefreshTrigger } from '../contexts/SidebarRefreshContext';
import { useBranchStore } from '../store/branchStore';
import { branchChangeEmitter } from '../lib/branchChangeEvent';
import {
  useAdminUsers,
  useUpdateUser,
  useLockUser,
  useDeleteUser,
  useUploadAvatar,
  useAddBranch,
  useRemoveBranch,
  useSetDefaultBranch,
  useDeleteInvitation,
} from '../hooks/useAdminUsers';

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isLocked: boolean;
  isDeleted: boolean;
  lockedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isPending?: false;
  avatar?: string | null;
  branches?: Array<{ id: string; name: string; location?: string; isDefault: boolean }>;
}

interface Invitation {
  id: string;
  code: string;
  email: string | null;
  name: null;
  role: string;
  branches: string[];
  status: string;
  expiresAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isPending: true;
}

type UserOrInvitation = User | Invitation;

// Helper function to get branch names from IDs
function getBranchNames(
  branchIdsOrJson: string[] | string,
  branchesData: Array<{ id: string; name: string }> = []
): string {
  let ids: string[] = [];

  if (typeof branchIdsOrJson === 'string') {
    try {
      ids = JSON.parse(branchIdsOrJson);
    } catch {
      return '-';
    }
  } else {
    ids = branchIdsOrJson;
  }

  if (!ids || ids.length === 0) return '-';

  // Map IDs to branch names if branchesData is provided
  if (branchesData.length > 0) {
    const branchMap = new Map(branchesData.map(b => [b.id, b.name]));
    const names = ids
      .map(id => branchMap.get(id))
      .filter((name): name is string => name !== undefined);
    return names.length > 0 ? names.join(', ') : '-';
  }

  return ids.join(', ');
}

export default function SettingsUsers() {
  const { user: currentUser, updateUser } = useAuth();
  const { triggerSidebarRefresh } = useSidebarRefreshTrigger();

  // React Query hooks for all operations
  const { data: adminData, isLoading: isLoadingUsers, refetch: refetchUsers } = useAdminUsers();
  const updateUserMutation = useUpdateUser();
  const lockUserMutation = useLockUser();
  const deleteUserMutation = useDeleteUser();
  const uploadAvatarMutation = useUploadAvatar();
  const addBranchMutation = useAddBranch();
  const removeBranchMutation = useRemoveBranch();
  const setDefaultBranchMutation = useSetDefaultBranch();
  const deleteInvitationMutation = useDeleteInvitation();

  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [visibleCodes, setVisibleCodes] = useState<Set<string>>(new Set());
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [branches, setBranches] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [codeHideTimers, setCodeHideTimers] = useState<Map<string, NodeJS.Timeout>>(new Map());
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'user' | 'invitation'; id: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingUser, setEditingUser] = useState<{ id: string; name: string; role: string } | null>(null);
  const [editError, setEditError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [selectedUserAvatarUrl, setSelectedUserAvatarUrl] = useState<string | null>(null);
  const [selectedUserAvatarLoading, setSelectedUserAvatarLoading] = useState(false);
  const [avatarImageLoaded, setAvatarImageLoaded] = useState(false);
  const [userPermissions, setUserPermissions] = useState<Record<string, any>>({});
  const [selectedUserBranches, setSelectedUserBranches] = useState<Array<{ id: string; name: string; isDefault: boolean }>>([]);
  const [isUpdatingBranches, setIsUpdatingBranches] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [lockingUserId, setLockingUserId] = useState<string | null>(null);
  const [branchError, setBranchError] = useState('');
  const [availableBranchesForAdd, setAvailableBranchesForAdd] = useState<Array<{ id: string; name: string }>>([]);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);
  const [branchToRemove, setBranchToRemove] = useState<string | null>(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fetchingUserIdRef = useRef<string | null>(null);

  // Sync users from React Query cache to local state
  useEffect(() => {
    if (adminData) {
      setUsers(adminData.users || []);
      setInvitations(adminData.invitations || []);
    }
  }, [adminData]);

  // Update selectedUser when clicking on a user in the table
  // Data is now fetched in handleOpenUserDetails before sidebar opens
  useEffect(() => {
    if (selectedUser) {
      const updatedUser = users.find(u => u.id === selectedUser.id);
      if (updatedUser) {
        console.log('Updated selectedUser from users list:', updatedUser);
        console.log('Avatar data for selectedUser:', { id: updatedUser.id, hasAvatar: !!updatedUser.avatar, avatarLength: updatedUser.avatar?.length });
        setSelectedUser(updatedUser);
      }
    }
  }, [users]);

  // Sync selectedUserBranches when selected user changes
  useEffect(() => {
    console.log('[SettingsUsers] selectedUser changed:', selectedUser?.id);
    if (selectedUser?.branches && Array.isArray(selectedUser.branches)) {
      setSelectedUserBranches(selectedUser.branches);
    } else {
      setSelectedUserBranches([]);
    }
  }, [selectedUser?.branches]);

  // Compute available branches for adding when selectedUser or branches changes
  useEffect(() => {
    if (selectedUser && branches.length > 0) {
      const assignedBranchIds = new Set(selectedUserBranches.map(b => b.id));
      const unassignedBranches = branches.filter(b => !assignedBranchIds.has(b.id));
      // Sort by name
      const sortedBranches = unassignedBranches.sort((a, b) => a.name.localeCompare(b.name));
      setAvailableBranchesForAdd(sortedBranches);
    } else {
      setAvailableBranchesForAdd([]);
    }
  }, [selectedUser, branches, selectedUserBranches]);

  // Fetch avatar URL when selected user changes
  useEffect(() => {
    let isMounted = true;

    const loadAvatarUrl = async () => {
      if (!selectedUser?.id) {
        setSelectedUserAvatarUrl(null);
        setSelectedUserAvatarLoading(false);
        setAvatarImageLoaded(false);
        return;
      }

      setAvatarImageLoaded(false);

      // Check shared cache first
      const cached = getCachedAvatarUrl(selectedUser.id);
      if (cached) {
        console.log('[SettingsUsers] Using cached avatar URL for user:', selectedUser.id);
        if (isMounted) {
          setSelectedUserAvatarUrl(cached);
          setSelectedUserAvatarLoading(false);
        }
        return;
      }

      // No cache, fetch from backend
      if (selectedUser.avatar) {
        if (isMounted) {
          setSelectedUserAvatarLoading(true);
        }

        try {
          console.log('[SettingsUsers] Fetching avatar URL for user:', selectedUser.id);
          const url = await getAvatarUrl(selectedUser.id);
          console.log('[SettingsUsers] Got avatar URL:', url ? 'success' : 'null');

          if (isMounted && url) {
            setSelectedUserAvatarUrl(url);
          }
        } catch (error) {
          console.error('[SettingsUsers] Failed to get avatar URL:', error);
          if (isMounted) {
            setSelectedUserAvatarUrl(null);
          }
        } finally {
          if (isMounted) {
            setSelectedUserAvatarLoading(false);
          }
        }
      } else {
        if (isMounted) {
          setSelectedUserAvatarUrl(null);
          setSelectedUserAvatarLoading(false);
        }
      }
    };

    loadAvatarUrl();

    return () => {
      isMounted = false;
    };
  }, [selectedUser?.id]);

  const fetchBranches = async () => {
    try {
      const res = await api.get<any>('/branches/all');
      setBranches(res.branches || []);
      console.log('[SettingsUsers] Fetched branches:', res.branches?.length);
    } catch (error) {
      console.error('[SettingsUsers] Failed to fetch branches:', error);
      // Still try to fetch via alternative endpoint or endpoint for creating invitations
    }
  };

  const fetchPermissions = async () => {
    try {
      // Fetch roles to get current user's permissions
      const rolesRes = await api.get<any>('/roles');
      if (rolesRes.roles && Array.isArray(rolesRes.roles)) {
        const userRole = rolesRes.roles.find((r: any) => r.name === currentUser?.role);
        if (userRole && userRole.permissions) {
          const perms = typeof userRole.permissions === 'string'
            ? JSON.parse(userRole.permissions)
            : userRole.permissions;
          setUserPermissions(perms);
          console.log('[SettingsUsers] Loaded permissions:', perms);
        }
      }
    } catch (error: any) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchPermissions();
  }, [currentUser?.role]);

  const handleLockUser = async (userId: string, isLocked: boolean) => {
    if (currentUser?.id === userId) {
      alert('You cannot lock/unlock your own account');
      return;
    }
    try {
      setIsLocking(true);
      setLockingUserId(userId);
      await lockUserMutation.mutateAsync({ userId, isLocked });
    } catch (error) {
      console.error('Failed to lock/unlock user:', error);
    } finally {
      setIsLocking(false);
      setLockingUserId(null);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (currentUser?.id === userId) {
      alert('You cannot change your own role');
      return;
    }
    try {
      setIsSavingUser(true);
      await updateUserMutation.mutateAsync({ userId, role: newRole });
    } catch (error) {
      console.error('Failed to change user role:', error);
      setEditError('Failed to change user role. ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleSaveName = async (userId: string, newName: string) => {
    if (!newName.trim()) {
      alert('Name cannot be empty');
      return;
    }
    try {
      setIsSavingName(true);
      await updateUserMutation.mutateAsync({ userId, name: newName });
      setEditingName(null);
      setEditError('');
    } catch (error) {
      console.error('[SettingsUsers] Failed to save user name:', error);
      setEditError('Failed to save name. ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSavingName(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (currentUser?.id === userId) {
      alert('You cannot delete your own account. Contact the company owner to delete your account.');
      return;
    }
    setDeleteConfirm({ type: 'user', id: userId });
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    setDeleteConfirm({ type: 'invitation', id: invitationId });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setIsDeleting(true);
      if (deleteConfirm.type === 'user') {
        await deleteUserMutation.mutateAsync(deleteConfirm.id);
        // Close sidebar if this is the selected user
        if (selectedUser?.id === deleteConfirm.id) {
          setSelectedUser(null);
        }
      } else {
        await deleteInvitationMutation.mutateAsync(deleteConfirm.id);
      }

      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete:', error);
      setDeleteConfirm(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    setAvatarError('');

    // Validate file size (max 5MB)
    const maxSizeBytes = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSizeBytes) {
      setAvatarError('File size must be less than 5MB');
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setAvatarError('Only JPEG or PNG files are allowed');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        // Validate dimensions (minimum 800x800)
        const img = new Image();
        img.onload = async () => {
          if (img.width < 800 || img.height < 800) {
            setAvatarError('Image must be at least 800x800 pixels');
            return;
          }

          // Upload to Supabase Storage via backend
          if (selectedUser) {
            try {
              setIsUploadingAvatar(true);
              console.log('Uploading avatar for user:', selectedUser.id);

              // Create FormData for multipart upload
              const formData = new FormData();
              formData.append('avatar', file);

              // Use fetch to handle multipart/form-data
              const response = await fetch(
                `${import.meta.env.VITE_API_URL || ''}/api/admin/users/${selectedUser.id}/avatar`,
                {
                  method: 'PUT',
                  body: formData,
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
                  },
                }
              );

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to upload avatar');
              }

              const data = await response.json();
              console.log('Avatar upload response:', data);

              // Update selected user with new avatar URL
              setSelectedUser({ ...selectedUser, avatar: data.user.avatar });

              // Clear avatar cache for this user so next access fetches fresh URL
              clearAvatarCache(selectedUser.id);

              // Refresh auth context to update sidebar immediately
              console.log('Fetching fresh data from /auth/me to update sidebar with avatar');
              const freshUserResponse = await fetch(
                `${import.meta.env.VITE_API_URL || ''}/api/auth/me`,
                {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
                  },
                }
              );
              const freshUserData = await freshUserResponse.json();
              console.log('Fresh user data with avatar:', freshUserData.user);

              // Update the auth context with fresh user data
              updateUser(freshUserData.user);
              console.log('Updated auth context with fresh avatar data');

              // Trigger sidebar refresh
              await triggerSidebarRefresh();
              console.log('Triggered sidebar refresh after avatar upload');

              // Refresh the avatar URL for the selected user
              if (selectedUser) {
                const newAvatarUrl = await getAvatarUrl(selectedUser.id);
                setSelectedUserAvatarUrl(newAvatarUrl);
              }

              setIsUploadingAvatar(false);
            } catch (error) {
              console.error('Failed to update avatar:', error);
              setAvatarError(error instanceof Error ? error.message : 'Failed to upload image');
              setIsUploadingAvatar(false);
            }
          }
        };
        img.onerror = () => {
          setAvatarError('Failed to load image');
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to process file:', error);
      setAvatarError('Failed to process image');
    }
  };

  const handleSaveUserEdit = async () => {
    if (!editingUser || !selectedUser) return;

    setEditError('');
    setIsSavingUser(true);
    console.log('[SettingsUsers] Starting handleSaveUserEdit for user:', editingUser.id);

    try {
      // Call API to update user
      console.log('[SettingsUsers] Calling PUT /admin/users with name:', editingUser.name, 'role:', editingUser.role);
      const response = await api.put(`/admin/users/${editingUser.id}`, {
        name: editingUser.name,
        role: editingUser.role,
      }) as any;

      console.log('[SettingsUsers] PUT response received:', response);

      // Update local state with the response
      const updatedUser = {
        ...selectedUser,
        name: response.user.name,
        role: response.user.role,
      };
      setSelectedUser(updatedUser);

      // Also update in the users list
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === editingUser.id
            ? { ...u, name: response.user.name, role: response.user.role }
            : u
        )
      );

      setEditingUser(null);

      // Refresh auth context to update sidebar immediately
      console.log('[SettingsUsers] Fetching fresh data from /auth/me to update sidebar');
      const freshUserResponse = await api.get<any>('/auth/me');
      console.log('[SettingsUsers] Fresh user data from /auth/me:', freshUserResponse);
      console.log('[SettingsUsers] Updated user name in fresh data:', freshUserResponse.user.name);
      // Update the auth context with fresh user data
      updateUser(freshUserResponse.user);
      console.log('[SettingsUsers] Updated auth context with fresh user data');
      console.log('[SettingsUsers] Current user in context should now be:', freshUserResponse.user);

      // Trigger sidebar refresh
      await triggerSidebarRefresh();
      console.log('[SettingsUsers] Triggered sidebar refresh after user edit');

      console.log('[SettingsUsers] handleSaveUserEdit completed successfully');
    } catch (error: any) {
      console.error('[SettingsUsers] Failed to save user:', error);
      setEditError(error.message || 'Failed to save changes');
    } finally {
      setIsSavingUser(false);
    }
  };

  const toggleCodeVisibility = (code: string) => {
    const newVisibleCodes = new Set(visibleCodes);
    if (newVisibleCodes.has(code)) {
      newVisibleCodes.delete(code);
      // Clear any existing timer
      const existingTimer = codeHideTimers.get(code);
      if (existingTimer) {
        clearTimeout(existingTimer);
        const newTimers = new Map(codeHideTimers);
        newTimers.delete(code);
        setCodeHideTimers(newTimers);
      }
    } else {
      newVisibleCodes.add(code);
      // Set timer to hide after 30 seconds
      const existingTimer = codeHideTimers.get(code);
      if (existingTimer) clearTimeout(existingTimer);

      const timer = setTimeout(() => {
        setVisibleCodes(prev => {
          const updated = new Set(prev);
          updated.delete(code);
          return updated;
        });
        const newTimers = new Map(codeHideTimers);
        newTimers.delete(code);
        setCodeHideTimers(newTimers);
      }, 30000);

      const newTimers = new Map(codeHideTimers);
      newTimers.set(code, timer);
      setCodeHideTimers(newTimers);
    }
    setVisibleCodes(newVisibleCodes);
  };

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      // Hide code after copy and reset feedback after 2 seconds
      setTimeout(() => {
        setVisibleCodes(prev => {
          const updated = new Set(prev);
          updated.delete(code);
          return updated;
        });
        setCopiedCode(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleOpenUserDetails = (user: User) => {
    console.log('[SettingsUsers] handleOpenUserDetails called for user:', user.id);
    // Cancel any pending fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Open sidebar immediately with current data from table
    setSelectedUser(user);
    setSelectedUserBranches(user.branches || []);
    fetchingUserIdRef.current = user.id;

    // Then fetch fresh data in the background to update if needed
    const fetchFreshData = async () => {
      try {
        const res = await api.get<any>('/admin/users');
        if (res.users) {
          const freshUser = res.users.find((u: User) => u.id === user.id);
          // Only update if we're still viewing this user (sidebar wasn't closed)
          if (freshUser && fetchingUserIdRef.current === user.id) {
            console.log('[SettingsUsers] Loaded fresh user data:', freshUser.id);
            setSelectedUser(freshUser);
            setSelectedUserBranches(freshUser.branches || []);
          }
        }
      } catch (error) {
        console.error('[SettingsUsers] Failed to fetch fresh user data:', error);
      }
    };

    // Fetch fresh data after a small delay to avoid blocking UI
    fetchTimeoutRef.current = setTimeout(fetchFreshData, 100);
  };

  const handleAddBranch = async (branchId: string) => {
    if (!selectedUser) return;
    setBranchError('');
    setIsUpdatingBranches(true);

    try {
      console.log('[SettingsUsers] Adding branch:', { userId: selectedUser.id, branchId });
      await addBranchMutation.mutateAsync({
        userId: selectedUser.id,
        branchId,
        isDefault: false,
      });
      console.log('[SettingsUsers] Branch added successfully');

      // Trigger sidebar refresh if this is the current user
      if (currentUser?.id === selectedUser.id) {
        triggerSidebarRefresh();
      }
    } catch (error: any) {
      console.error('Failed to add branch:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        data: error.data,
        response: error.response?.statusText
      });
      setBranchError(error.message || 'Failed to add branch');
    } finally {
      setIsUpdatingBranches(false);
    }
  };

  const handleRemoveBranch = async (branchId: string) => {
    if (!selectedUser) return;
    setBranchError('');
    setIsUpdatingBranches(true);

    try {
      await removeBranchMutation.mutateAsync({
        userId: selectedUser.id,
        branchId,
      });

      // Trigger sidebar refresh if this is the current user
      if (currentUser?.id === selectedUser.id) {
        triggerSidebarRefresh();
      }

      setBranchToRemove(null);
    } catch (error: any) {
      console.error('Failed to remove branch:', error);
      setBranchError(error.message || 'Failed to remove branch');
    } finally {
      setIsUpdatingBranches(false);
    }
  };

  const handleSetDefaultBranch = async (branchId: string) => {
    if (!selectedUser) return;
    setBranchError('');
    setIsUpdatingBranches(true);

    try {
      await setDefaultBranchMutation.mutateAsync({
        userId: selectedUser.id,
        branchId,
      });

      // Trigger sidebar refresh if this is the current user
      if (currentUser?.id === selectedUser.id) {
        triggerSidebarRefresh();

        // Get the full branch object from selectedUserBranches
        const selectedBranch = selectedUserBranches.find(b => b.id === branchId);
        if (selectedBranch) {
          console.log('[SettingsUsers] Branch changed for current user to:', branchId, selectedBranch.name);
          // Update Zustand store with the new active branch
          const { setActiveBranch } = useBranchStore.getState();
          setActiveBranch(selectedBranch);

          // Emit branch change event to notify all components
          console.log('[SettingsUsers] Emitting branch change event');
          branchChangeEmitter.emit(selectedBranch);
        }
      }
    } catch (error: any) {
      console.error('Failed to set default branch:', error);
      setBranchError(error.message || 'Failed to set default branch');
    } finally {
      setIsUpdatingBranches(false);
    }
  };

  // Combine users and invitations into one list, sorted by creation date
  const allItems: UserOrInvitation[] = [
    ...invitations.map(inv => ({ ...inv, isPending: true } as Invitation)),
    ...users.filter(u => !u.isDeleted && (currentUser?.role === 'company_owner' || u.role !== 'company_owner')),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (isLoadingUsers) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Check if user has permission to view users
  const canViewUsers = userPermissions.users?.view === true;

  if (!canViewUsers && users.length === 0 && !isLoadingUsers) {
    return (
      <div className="p-8">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Users</h1>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-blue-800">
              You do not have permission to view users.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const canCreateInvitation = userPermissions.users?.create === true;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 mt-1">Manage team members and access</p>
        </div>
        <button
          onClick={() => setShowInviteForm(!showInviteForm)}
          disabled={!canCreateInvitation}
          title={!canCreateInvitation ? "You don't have permission to create invitations" : ""}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            canCreateInvitation
              ? 'bg-cyan-600 text-white hover:bg-cyan-700 cursor-pointer'
              : 'hidden'
          }`}
        >
          <Plus className="h-5 w-5" />
          Add User
        </button>
      </div>

      {/* Permission Denied Message */}
      {!isLoadingUsers && users.length === 0 && invitations.length === 0 && !userPermissions.users?.view && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <p className="text-blue-900">
            You don&apos;t have permission to view users. Contact your company owner to enable the &quot;View Users&quot; permission.
          </p>
        </div>
      )}

      {/* Invite Form */}
      {showInviteForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Create User Invitation</h2>
          <InviteUserForm
            branches={branches}
            onInvitationCreated={(invitation) => {
              // Add the newly created invitation to the local state immediately
              if (invitation && invitation.id) {
                const invitationWithPending = {
                  ...invitation,
                  isPending: true,
                  name: null,
                } as Invitation;
                setInvitations(prev => [invitationWithPending, ...prev]);
              }
            }}
            onSuccess={(invitation) => {
              // Close the form when the user closes the success panel
              setShowInviteForm(false);
              refetchUsers();
            }}
          />
        </div>
      )}

      {/* Users & Invitations List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">
            Team Members ({allItems.length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Branches</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Code</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Expires</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {allItems.map((item) => {
                const isPending = 'isPending' in item && item.isPending;
                const isUser = !isPending;
                const user = isUser ? (item as User) : null;
                const invitation = isPending ? (item as Invitation) : null;

                return (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 ${isUser ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      isUser && handleOpenUserDetails(user!);
                    }}
                  >
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isUser ? (user?.name || '-') : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {isUser ? user?.email : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                      {item.role.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {isUser ? (user && user.branches && user.branches.length > 0 ? user.branches.map(b => b.name).join(', ') : '-') : getBranchNames(invitation.branches, branches)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                      {invitation ? (
                        <div className="flex items-center gap-2">
                          <span>{visibleCodes.has(invitation.code) ? invitation.code : '••••••'}</span>
                          <button
                            onClick={() => toggleCodeVisibility(invitation.code)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            title="Toggle visibility"
                          >
                            {visibleCodes.has(invitation.code) ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                          {visibleCodes.has(invitation.code) && (
                            <button
                              onClick={() => copyToClipboard(invitation.code)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title="Copy code"
                            >
                              {copiedCode === invitation.code ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {isPending && invitation?.expiresAt ? (
                        new Date(invitation.expiresAt).toLocaleDateString() + ' ' + new Date(invitation.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {isPending ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      ) : user?.isLocked ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Locked
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                      {isUser ? (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLockUser(user!.id, !user!.isLocked);
                            }}
                            disabled={currentUser?.id === user!.id || userPermissions.users?.lockUnlock !== true || (isLocking && lockingUserId === user!.id)}
                            className={`transition-colors ${
                              currentUser?.id === user!.id || userPermissions.users?.lockUnlock !== true
                                ? 'text-gray-300 cursor-not-allowed'
                                : (isLocking && lockingUserId === user!.id)
                                ? 'text-gray-400'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                            title={currentUser?.id === user!.id ? 'Cannot lock your own account' : (userPermissions.users?.lockUnlock !== true ? 'No permission to lock users' : (user!.isLocked ? 'Unlock user' : 'Lock user'))}
                          >
                            {isLocking && lockingUserId === user!.id ? (
                              <div className="h-4 w-4 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" />
                            ) : (
                              user!.isLocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(user!.id);
                            }}
                            disabled={currentUser?.id === user!.id || userPermissions.users?.delete !== true}
                            className={`transition-colors ${
                              currentUser?.id === user!.id || userPermissions.users?.delete !== true
                                ? 'text-red-300 cursor-not-allowed'
                                : 'text-red-600 hover:text-red-800'
                            }`}
                            title={currentUser?.id === user!.id ? 'Cannot delete your own account' : (userPermissions.users?.delete !== true ? 'No permission to delete users' : 'Delete user')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteInvitation(invitation!.id);
                          }}
                          disabled={userPermissions.users?.delete !== true}
                          className={`transition-colors ${
                            userPermissions.users?.delete !== true
                              ? 'text-red-300 cursor-not-allowed'
                              : 'text-red-600 hover:text-red-800'
                          }`}
                          title={userPermissions.users?.delete !== true ? 'No permission to delete invitations' : 'Delete invitation'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Details Sidebar */}
      {selectedUser && (
        <>
          {/* Overlay - simple click to close */}
          <div
            data-testid="sidebar-overlay"
            className="fixed inset-0 z-40 bg-black/50"
            onMouseDown={(e) => {
              // Use mousedown to close immediately
              console.log('[SettingsUsers] Overlay mousedown - closing sidebar');
              e.preventDefault();
              e.stopPropagation();
              // Cancel any pending fetch before closing
              if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
                fetchTimeoutRef.current = null;
              }
              // Clear the fetching user ID so in-flight requests won't update
              fetchingUserIdRef.current = null;
              setSelectedUser(null);
              setBranchError('');
            }}
            onClick={(e) => {
              // Also handle click to be safe
              console.log('[SettingsUsers] Overlay click - should already be closed');
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          {/* Sidebar */}
          <div
            data-testid="user-details-sidebar"
            className="fixed right-0 top-0 bottom-0 w-[520px] bg-white z-50 overflow-y-auto flex flex-col"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="p-6 flex-1 flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900">User Details</h2>
                <button
                  onClick={() => {
                    setSelectedUser(null);
                    setBranchError('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Permission Check: Show message if viewing other user without permission */}
              {currentUser?.id !== selectedUser.id && !userPermissions.users?.view ? (
                <div className="flex items-center gap-3 py-4 px-3 bg-gray-50 rounded-lg border border-gray-200 mb-6">
                  <Lock className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-900 font-medium capitalize">No Permissions</p>
                    <p className="text-xs text-gray-500">You don&apos;t have permission to view other users</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Profile Section - Photo left, Details middle, Status right */}
                  <div className="mb-6 pb-6 border-b border-gray-100">
                    <div className="flex items-start gap-4">
                  {/* Photo with clickable upload indicator */}
                  <div className="relative flex-shrink-0">
                    {(userPermissions.profile?.editOwn === true && currentUser?.id === selectedUser.id) || userPermissions.profile?.editOthers === true || userPermissions.users?.edit === true ? (
                      <label className="cursor-pointer block">
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-white text-lg font-bold overflow-hidden relative ${
                          selectedUserAvatarUrl && !selectedUserAvatarLoading && !isUploadingAvatar
                            ? 'bg-gray-200'
                            : 'bg-gradient-to-br from-cyan-400 to-blue-600'
                        }`}>
                          {selectedUserAvatarLoading || isUploadingAvatar ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-cyan-400 to-blue-600">
                              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : selectedUserAvatarUrl ? (
                            <img
                              src={selectedUserAvatarUrl}
                              alt={selectedUser.name || 'User'}
                              className="w-full h-full object-cover transition-all duration-300"
                              onLoad={(e) => {
                                setAvatarImageLoaded(true);
                              }}
                              style={{ filter: avatarImageLoaded ? 'blur(0px)' : 'blur(10px)' }}
                            />
                          ) : (
                            <UserCircle className="h-8 w-8" />
                          )}
                        </div>
                        {/* Small circle indicator at bottom-right corner */}
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-cyan-600 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                          <Upload className="h-2.5 w-2.5 text-white" />
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/jpeg,image/png"
                          disabled={isUploadingAvatar}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleAvatarUpload(file);
                            }
                          }}
                        />
                      </label>
                    ) : (
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-white text-lg font-bold overflow-hidden relative ${
                        selectedUserAvatarUrl && !selectedUserAvatarLoading && !isUploadingAvatar
                          ? 'bg-gray-200'
                          : 'bg-gradient-to-br from-cyan-400 to-blue-600'
                      }`}>
                        {selectedUserAvatarLoading || isUploadingAvatar ? (
                          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-cyan-400 to-blue-600">
                            <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : selectedUserAvatarUrl ? (
                          <img
                            src={selectedUserAvatarUrl}
                            alt={selectedUser.name || 'User'}
                            className="w-full h-full object-cover transition-all duration-300"
                            onLoad={(e) => {
                              setAvatarImageLoaded(true);
                            }}
                            style={{ filter: avatarImageLoaded ? 'blur(0px)' : 'blur(10px)' }}
                          />
                        ) : (
                          <UserCircle className="h-8 w-8" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* User details - middle */}
                  <div className="flex-1 min-w-0">
                    {editingName !== null ? (
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 text-xs"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveName(selectedUser.id, editingName)}
                          disabled={isSavingName}
                          className="px-2 py-1 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors"
                          title="Save"
                        >
                          <Save className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => setEditingName(null)}
                          className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-gray-900 truncate">{selectedUser.name || '-'}</p>
                        <p className="text-xs text-gray-500 truncate">{selectedUser.email}</p>
                      </>
                    )}

                    {!editingName && ((userPermissions.profile?.editOwn === true && currentUser?.id === selectedUser.id) || userPermissions.profile?.editOthers === true || userPermissions.users?.edit === true) && (
                      <button
                        onClick={() => setEditingName(selectedUser.name || '')}
                        className="text-cyan-600 hover:text-cyan-700 text-xs font-medium mt-1 transition-colors"
                      >
                        Edit
                      </button>
                    )}

                    {avatarError && (
                      <p className="text-xs text-red-600 mt-1">{avatarError}</p>
                    )}
                  </div>

                  {/* Status - right side */}
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
                    {selectedUser.isLocked ? (
                      <span className="inline-block px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-700">
                        Locked
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-700">
                        Active
                      </span>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Created {new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Role Section - With border-b */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-3">Role</h3>
                {/* Always show the role */}
                {currentUser?.id === selectedUser.id ? (
                  // Current user - always read-only
                  <div className="flex items-center gap-3 py-4 px-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-gray-400 flex-shrink-0">
                      <Lock className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-900 font-medium capitalize">{selectedUser.role.replace('_', ' ')}</p>
                      <p className="text-xs text-gray-500">Your current role</p>
                    </div>
                  </div>
                ) : selectedUser.role === 'company_owner' ? (
                  // Company owner - cannot be changed
                  <div className="flex items-center gap-3 py-4 px-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-gray-400 flex-shrink-0">
                      <Lock className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-900 font-medium capitalize">{selectedUser.role.replace('_', ' ')}</p>
                      <p className="text-xs text-gray-500">Company owner role cannot be changed</p>
                    </div>
                  </div>
                ) : userPermissions.users?.changeRole === true ? (
                  // Other user with changeRole permission - editable
                  <select
                    value={selectedUser.role}
                    onChange={(e) => handleRoleChange(selectedUser.id, e.target.value)}
                    disabled={isSavingUser}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 bg-white"
                  >
                    <option value="manager">Manager</option>
                    <option value="technician">Technician</option>
                    <option value="operator">Operator</option>
                  </select>
                ) : (
                  // Other user without changeRole permission - read-only
                  <div className="flex items-center gap-3 py-4 px-3 bg-gray-50 rounded-lg border border-gray-200">
                    <Lock className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-900 font-medium capitalize">{selectedUser.role.replace('_', ' ')}</p>
                      <p className="text-xs text-gray-500">You don&apos;t have permission to change roles</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Branches Section - No background */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-gray-700 uppercase">Branches</h3>
                  {/* Add Branch button: only for other users with addBranch permission */}
                  {(currentUser?.id !== selectedUser.id && userPermissions.users?.addBranch === true) && availableBranchesForAdd.length > 0 && (
                    <div className="flex items-center gap-2">
                      {isUpdatingBranches && (
                        <div className="h-3 w-3 border-2 border-cyan-400 border-t-cyan-600 rounded-full animate-spin" />
                      )}
                      <select
                        onChange={(e) => {
                          console.log('[SettingsUsers] Branch dropdown changed:', e.target.value);
                          if (e.target.value) {
                            console.log('[SettingsUsers] Calling handleAddBranch with branchId:', e.target.value);
                            handleAddBranch(e.target.value);
                            e.target.value = '';
                          }
                        }}
                        disabled={isUpdatingBranches}
                        className="text-xs px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-50 bg-white"
                      >
                        <option value="">+ Add</option>
                        {availableBranchesForAdd.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Permission check for viewing branches */}
                {(currentUser?.id !== selectedUser.id && !userPermissions.users?.viewBranches) ? (
                  <div className="flex items-center gap-3 py-4 px-3 bg-gray-50 rounded-lg border border-gray-200">
                    <Lock className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-900 font-medium capitalize">No Permissions</p>
                      <p className="text-xs text-gray-500">You don&apos;t have permission to view branches</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {!selectedUserBranches || selectedUserBranches.length === 0 ? (
                      <p className="text-xs text-gray-600">No branches assigned</p>
                    ) : selectedUserBranches.length > 0 ? (
                      <div className="space-y-2">
                        {[...selectedUserBranches].sort((a, b) => a.name.localeCompare(b.name)).map((branch) => (
                          <div key={branch.id} className="flex items-center gap-2">
                            <button
                              onClick={() => !branch.isDefault && handleSetDefaultBranch(branch.id)}
                              disabled={isUpdatingBranches || branch.isDefault}
                              className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${
                                branch.isDefault
                                  ? 'bg-gray-100 cursor-default'
                                  : 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                              }`}
                            >
                              {isUpdatingBranches && (
                                <div className="h-3 w-3 border-2 border-cyan-400 border-t-cyan-600 rounded-full animate-spin flex-shrink-0" />
                              )}
                              <span className="text-gray-900 font-medium truncate">{branch.name}</span>
                              {branch.isDefault && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-700 whitespace-nowrap">
                                  Default
                                </span>
                              )}
                            </button>
                            {selectedUserBranches.length > 1 && currentUser?.id !== selectedUser.id && (userPermissions.users?.removeBranch === true) && (
                              <button
                                onClick={() => setBranchToRemove(branch.id)}
                                disabled={isUpdatingBranches}
                                className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 p-1 flex-shrink-0"
                                title="Remove"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-600">No branches</p>
                    )}
                  </>
                )}

                {branchError && (
                  <p className="text-xs text-red-600 mt-2">{branchError}</p>
                )}
              </div>

              {/* Actions - Bigger buttons with margin at bottom */}
              <div className="flex gap-3 mt-auto pb-4">
                {/* Lock/Unlock Button - Controlled by users.lockUnlock permission */}
                {userPermissions.users?.lockUnlock === true && currentUser?.id !== selectedUser.id && (
                  <button
                    onClick={() => handleLockUser(selectedUser.id, !selectedUser.isLocked)}
                    disabled={isLocking}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all font-medium text-sm ${
                      selectedUser.isLocked
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
                    }`}
                  >
                    {isLocking ? (
                      <>
                        <div className="h-5 w-5 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin" />
                        {selectedUser.isLocked ? 'Unlocking...' : 'Locking...'}
                      </>
                    ) : (
                      <>
                        {selectedUser.isLocked ? (
                          <>
                            <Unlock className="h-5 w-5" />
                            Unlock
                          </>
                        ) : (
                          <>
                            <Lock className="h-5 w-5" />
                            Lock
                          </>
                        )}
                      </>
                    )}
                  </button>
                )}

                {/* Delete Button - Controlled by users.delete permission */}
                {userPermissions.users?.delete === true && currentUser?.id !== selectedUser.id && (
                  <button
                    onClick={() => handleDeleteUser(selectedUser.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all font-medium text-sm"
                  >
                    <Trash2 className="h-5 w-5" />
                    Delete
                  </button>
                )}
              </div>

              {editError && (
                <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-600">{editError}</p>
                </div>
              )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Branch Removal Confirmation Modal */}
      {branchToRemove && selectedUser && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setBranchToRemove(null)} />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-50 w-96 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Remove Branch</h3>
            <div className="text-gray-600 text-sm mb-6 space-y-2">
              <p>Are you sure you want to remove this branch from {selectedUser.name}?</p>
              <p className="text-xs text-gray-500">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setBranchToRemove(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveBranch(branchToRemove)}
                disabled={isUpdatingBranches}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isUpdatingBranches ? '...' : 'Remove'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setDeleteConfirm(null)} />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-50 w-96 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Confirm Delete</h3>
            <div className="text-gray-600 text-sm mb-6 space-y-2">
              {deleteConfirm.type === 'user' ? (
                <>
                  <p>Are you sure you want to delete this user?</p>
                  <p>This action cannot be undone.</p>
                </>
              ) : (
                <>
                  <p>Are you sure you want to delete this invitation?</p>
                  <p>This action cannot be undone.</p>
                </>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-wait"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Invite User Form Component
function InviteUserForm({
  branches,
  onSuccess,
  onInvitationCreated,
}: {
  branches: Array<{ id: string; name: string }>;
  onSuccess: (invitation?: any) => void;
  onInvitationCreated?: (invitation: any) => void;
}) {
  const [role, setRole] = useState('technician');
  const [expiresIn, setExpiresIn] = useState('none');
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [defaultBranchId, setDefaultBranchId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [invitationData, setInvitationData] = useState<any>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [codeVisible, setCodeVisible] = useState(true);
  const [secondsRemaining, setSecondsRemaining] = useState(30);
  const [hideTimer, setHideTimer] = useState<NodeJS.Timeout | null>(null);
  const [countdownTimer, setCountdownTimer] = useState<NodeJS.Timeout | null>(null);

  const toggleBranch = (branchId: string) => {
    setSelectedBranches(prev =>
      prev.includes(branchId)
        ? prev.filter(id => id !== branchId)
        : [...prev, branchId]
    );
  };

  const copyInvitationCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(true);
      setTimeout(() => {
        setCopiedCode(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  // Auto-hide code after 30 seconds when it becomes visible
  React.useEffect(() => {
    if (codeVisible && invitationCode) {
      // Clear existing timers
      if (hideTimer) clearTimeout(hideTimer);
      if (countdownTimer) clearInterval(countdownTimer);

      setSecondsRemaining(30);

      // Set countdown timer
      const countdown = setInterval(() => {
        setSecondsRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdown);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Set timer to hide code and close form after 30 seconds
      const timer = setTimeout(() => {
        setCodeVisible(false);
        clearInterval(countdown);
        // Close the entire invitation card
        setTimeout(() => {
          setInvitationCode('');
          setExpiresAt(null);
          setRole('technician');
          setExpiresIn('none');
          setSelectedBranches([]);
          setDefaultBranchId('');
          setCopiedCode(false);
          onSuccess(invitationData); // Pass the invitation data
        }, 500);
      }, 30000);

      setCountdownTimer(countdown);
      setHideTimer(timer);
    }

    return () => {
      if (hideTimer) clearTimeout(hideTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [codeVisible, invitationCode, invitationData, onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate that at least one branch is selected
    if (selectedBranches.length === 0) {
      setError('Please select at least one branch');
      return;
    }

    // Validate that a default branch is selected
    if (!defaultBranchId) {
      setError('Please select a default branch');
      return;
    }

    // Validate that default branch is in selected branches
    if (!selectedBranches.includes(defaultBranchId)) {
      setError('Default branch must be one of the selected branches');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/admin/invitations', {
        role,
        branchIds: selectedBranches,
        defaultBranchId,
        expiresIn,
      }) as any;

      // Copy code to clipboard
      const code = response.code;
      setInvitationCode(code);
      // Get expiresAt from invitation object or fallback to top level
      const expiresAtValue = response.invitation?.expiresAt || response.expiresAt || null;
      setExpiresAt(expiresAtValue);
      setCodeVisible(true);
      setSecondsRemaining(30);

      // Store the full invitation object for later use in onSuccess
      const invitation = response.invitation;
      setInvitationData(invitation);

      // Call the invitation created callback immediately to add it to the list
      if (onInvitationCreated) {
        onInvitationCreated(invitation);
      }

      try {
        await navigator.clipboard.writeText(code);
        setCopiedCode(true);
      } catch (clipboardError) {
        console.error('Failed to copy to clipboard:', clipboardError);
        setCopiedCode(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  if (invitationCode) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-green-800 font-semibold">Invitation Created!</h3>
            </div>
            <button
              onClick={() => {
                setInvitationCode('');
                setExpiresAt(null);
                setCodeVisible(true);
                setSecondsRemaining(30);
                setRole('technician');
                setExpiresIn('none');
                setSelectedBranches([]);
                setCopiedCode(false);
                onSuccess(invitationData);
              }}
              className="text-gray-500 hover:text-gray-700 text-lg font-bold"
              title="Close"
            >
              ✕
            </button>
          </div>
          <p className="text-green-700 text-sm mb-4">Share this code with the user to join your company.</p>

          <div className="bg-white border border-green-200 rounded p-4 mb-4">
            <p className="text-xs text-gray-500 mb-3">Invitation Code</p>
            <div className="flex items-center gap-2 mb-3">
              <p className="text-3xl font-mono font-bold text-gray-900 tracking-widest flex-1">
                {codeVisible ? invitationCode : '••••••'}
              </p>
              <button
                onClick={() => setCodeVisible(!codeVisible)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2"
                title={codeVisible ? 'Hide code' : 'Show code'}
              >
                {codeVisible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
              {codeVisible && (
                <button
                  onClick={() => copyInvitationCode(invitationCode)}
                  className={`transition-colors p-2 ${copiedCode ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Copy code"
                >
                  {copiedCode ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              )}
            </div>
            {codeVisible && (
              <p className="text-xs text-gray-500 mb-3">
                Auto-hides in <span className="font-semibold text-gray-700">{secondsRemaining}s</span>
              </p>
            )}
            {expiresAt && (
              <p className="text-xs text-gray-600">
                Expires: <span className="font-semibold">{new Date(expiresAt).toLocaleString()}</span>
              </p>
            )}
            {!expiresAt && (
              <p className="text-xs text-gray-600">
                Expires: <span className="font-semibold">Never</span>
              </p>
            )}
          </div>

          {copiedCode && (
            <p className="text-green-700 text-sm font-medium">✓ Code copied to clipboard!</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="manager">Manager</option>
          <option value="technician">Technician</option>
          <option value="operator">Operator</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Branches *</label>
        <div className="space-y-2">
          {branches.map(branch => (
            <label key={branch.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedBranches.includes(branch.id)}
                onChange={() => {
                  toggleBranch(branch.id);
                  // If unchecking the default branch, clear it
                  if (defaultBranchId === branch.id) {
                    setDefaultBranchId('');
                  }
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">{branch.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Default Branch Selection - Only show if branches selected */}
      {selectedBranches.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Default Branch *</label>
          <div className="space-y-2">
            {branches
              .filter(branch => selectedBranches.includes(branch.id))
              .map(branch => (
                <label key={branch.id} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="defaultBranch"
                    value={branch.id}
                    checked={defaultBranchId === branch.id}
                    onChange={(e) => setDefaultBranchId(e.target.value)}
                    className="rounded-full border-gray-300"
                  />
                  <span className="text-sm text-gray-700">{branch.name}</span>
                  {defaultBranchId === branch.id && (
                    <span className="ml-auto inline-block px-2 py-1 rounded text-xs font-medium bg-cyan-100 text-cyan-700">
                      Default
                    </span>
                  )}
                </label>
              ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Expiration *</label>
        <select
          value={expiresIn}
          onChange={(e) => setExpiresIn(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="none">No Expiration</option>
          <option value="1day">1 Day</option>
          <option value="2days">2 Days</option>
          <option value="3days">3 Days</option>
          <option value="7days">7 Days</option>
        </select>
      </div>

      <div className="flex gap-3 justify-end pt-4">
        <button
          type="button"
          onClick={() => {
            setRole('technician');
            setExpiresIn('none');
            setSelectedBranches([]);
            setDefaultBranchId('');
            setError('');
            onSuccess(null);
          }}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create'}
        </button>
      </div>
    </form>
  );
}
